import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './button';
import { ArrowLeftIcon, DocumentTextIcon, BanknotesIcon, UserCircleIcon, DocumentCheckIcon, ArrowPathIcon, EnvelopeIcon } from '@heroicons/react/24/solid';
import { NotifyFianzaDialog } from './NotifyFianzaDialog';
import { CheckCircleIcon, CameraIcon, ShieldExclamationIcon, XMarkIcon, ArchiveBoxIcon } from '@heroicons/react/24/outline';
import { toPng } from 'html-to-image';
import { ref, deleteObject, getDownloadURL, uploadBytes } from 'firebase/storage';
import { storage } from '../firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import CompactFileReceiver from './CompactFileReceiver';
import { useUser } from './UserContext';
import { usePortalConfig } from '../hooks/usePortalConfig';
import { logBitacora } from '../utils/bitacora';
import { mutateFianza } from '../utils/contractWrites';
import SolicitarEndosoDialog from './SolicitarEndosoDialog';
import PendientesPanel from './PendientesPanel';
import OtActivas from './ot/OtActivas';
import { useRegistrarContextoPendiente } from '../contexts/PendienteContexto';
import { PENDIENTE_TIPOS } from '../utils/pendientes';
import { parseXmlFianza, parseXmlFactura, validateFianzaFactura } from '../utils/xmlParsers';
import { namesMatch } from '../utils/convenioCompliance';
import { formatNumeroFianza, getEmisorNombre } from '../utils/calculations';
import { rfcsMatch } from '../utils/identityMismatch';
import XmlConfirmationDialog from './XmlConfirmationDialog';
import { EndosoDialog } from './EndosoDialog';
import { SustituirFianzaDialog } from './SustituirFianzaDialog';
import { RenovarFianzaDialog } from './RenovarFianzaDialog';
import { ContratoView } from './ContratoView';
import { setFacturaPagada, setBeneficiarioConvenio } from '../api/comisiones';
import { ComisionLedgerPanel } from './ComisionLedgerPanel';
import { ArchivarFianzaDialog } from './ArchivarFianzaDialog';
import {
  ensureEventos, ensureFacturas, FACTURA_MODE_LABELS, getEstadoFianza, ESTADO_FIANZA,
  formatCambioValor, formatEventoTipos, getEndososAbiertos, clasificarEndosoPendiente,
  cancelarEndosoPendiente,
  EVENTO_TIPO_LABELS as EVENTO_TIPO_LABELS_LOCAL, EVENTO_TIPOS,
  getFacturasVigentes, getValidPrima,
} from '../utils/fianzaEventos';
import { fianzaStoragePath } from '../utils/contratoStorage';

/**
 * FianzaView Component
 * Displays detailed fianza information with ability to view/manage files
 * Shows client and contract information
 * Allows file deletion and replacement (requires 2 clicks for safety)
 */
const EXPORT_ENABLED = process.env.REACT_APP_ENABLE_EXPORT === 'true';

// Labels for per-event document links in the fianza timeline.
const EVENT_FILE_LABELS = {
  fianzaPdf: 'PDF fianza',
  fianzaXml: 'XML fianza',
  facturaPdf: 'PDF factura',
  facturaXml: 'XML factura',
  comprobantePago: 'Comprobante de pago',
  endosoPdf: 'PDF endoso',
};

const ESTADO_LABELS = {
  [ESTADO_FIANZA.CANCELADA]: 'Cancelada',
  [ESTADO_FIANZA.ANULADA]: 'Anulada',
  [ESTADO_FIANZA.SUSTITUIDA]: 'Sustituida',
};

export function FianzaView({ isOpen, onClose, fianza, clientData, contratoData, onUpdate, convenio, beneficiariosById }) {
  // Nested ContratoView for the associated contract. Circular import with
  // ContratoView is safe: both are hoisted function declarations only
  // dereferenced at render time.
  const [showContrato, setShowContrato] = useState(false);
  const navigate = useNavigate();
  const isConvenioContract = Boolean(contratoData?.convenioId);
  const beneficiario = isConvenioContract && contratoData?.beneficiarioId
    ? beneficiariosById?.[contratoData.beneficiarioId]
    : null;
  const fiado = isConvenioContract
    ? (contratoData?.proveedor?.value || contratoData?.proveedor || null)
    : null;
  const proyectoNombre = isConvenioContract && contratoData?.proyectoId
    ? ((convenio?.proyectos || []).find((p) => p.id === contratoData.proyectoId)?.nombre || null)
    : null;

  // Build bitácora metadata that routes correctly to the convenio Historial
  // when this fianza belongs to a convenio contract.
  const buildBitacoraMeta = (description, extra = {}) => {
    if (isConvenioContract) {
      return {
        convenioId: contratoData?.convenioId,
        convenioName: convenio?.nombre || null,
        beneficiarioNombre: beneficiario?.nombre || null,
        proveedor: fiado || null,
        contratoId: fianza?.contratoId,
        fianzaId: fianza?.id,
        description,
        ...extra,
      };
    }
    return {
      clientId: clientData?.id || null,
      clientName: clientData?.nombreEmpresa || 'Sin nombre',
      contratoId: fianza?.contratoId,
      fianzaId: fianza?.id,
      description,
      ...extra,
    };
  };
  const [replacingFile, setReplacingFile] = useState(null); // Track which file is being replaced
  const [isUpdating, setIsUpdating] = useState(false);
  const [showEndoso, setShowEndoso] = useState(false);
  const [showSustituir, setShowSustituir] = useState(false);
  const [showRenovar, setShowRenovar] = useState(false);
  const [showSolicitar, setShowSolicitar] = useState(false);
  // Set when "Registrar" is clicked on a specific pending request, so applying
  // the endoso closes that request in the same write.
  const [endosoRequeridoId, setEndosoRequeridoId] = useState(null);
  const [showFacturasPago, setShowFacturasPago] = useState(false);
  const [liveFianzaData, setLiveFianzaData] = useState(fianza); // Live fianza data from Firestore
  // Endosos this fianza was asked for and that have not been applied yet.
  const endososAbiertos = getEndososAbiertos(liveFianzaData);

  // Close a request without an endoso. Same write path as registering one
  // (mutateFianza on the contract), so the strip and the Pendientes list
  // update from the contract listener.
  const cancelarSolicitudEndoso = async (p) => {
    if (!fianza?.contratoId || !fianza?.id) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm('¿Cancelar esta solicitud de endoso?')) return;
    setIsUpdating(true);
    try {
      const ts = Date.now();
      await mutateFianza(db, fianza.contratoId, fianza.id, (current) => ({
        ...cancelarEndosoPendiente(current, p.id, null, ts),
        updatedAt: ts,
      }));
      try {
        await logBitacora({
          action: 'UPDATE',
          collection: 'contracts',
          documentId: fianza.contratoId,
          user,
          userData,
          metadata: {
            contratoId: fianza.contratoId,
            fianzaId: fianza.id,
            endosoPendienteId: p.id,
            description: `Canceló solicitud de endoso en fianza #${fianza.numeroFianza || 'sin número'}`,
          },
        });
      } catch (e) {
        console.error('bitacora cancelar endoso pendiente failed', e);
      }
      if (onUpdate) await onUpdate();
    } catch (e) {
      console.error('cancelar solicitud endoso failed', e);
    } finally {
      setIsUpdating(false);
    }
  };
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingUpload, setPendingUpload] = useState(null); // { fileKey, file, parsedData, warnings }
  const [parseError, setParseError] = useState(null);
  const [parseErrorKey, setParseErrorKey] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [isNotifyOpen, setIsNotifyOpen] = useState(false);
  const { user, userData, isClient } = useUser();

  // Tell the bolita what is on screen. Registered after ContratoView's, so
  // while this modal is open the fianza is the context.
  useRegistrarContextoPendiente(Boolean(isOpen && !isClient && liveFianzaData?.id), {
    clienteId: clientData?.id || null,
    contratoId: fianza?.contratoId || contratoData?.id || null,
    fianzaId: liveFianzaData?.id || null,
    numeroFianza: liveFianzaData?.numeroFianza || '',
    contratoTitulo: contratoData?.tituloContrato?.value || '',
    afianzadoraRaw: getEmisorNombre(liveFianzaData) || null,
  });
  const { hasTarifa } = usePortalConfig();
  const exportRef = useRef(null);
  const [isExportingView, setIsExportingView] = useState(false);

  const handleExportView = useCallback(async () => {
    if (!exportRef.current) return;
    setIsExportingView(true);
    await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
    await document.fonts.ready;
    try {
      const dataUrl = await toPng(exportRef.current, {
        pixelRatio: 2,
        backgroundColor: '#f4f4f5',
      });
      const numero = liveFianzaData.numeroFianza || 'fianza';
      const link = document.createElement('a');
      link.download = `${numero}-${new Date().toISOString().slice(0, 10)}.png`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setIsExportingView(false);
    }
  }, [liveFianzaData]);

  // Set up live listener for fianza updates
  useEffect(() => {
    if (!isOpen || !fianza?.contratoId) return;

    const contractDocRef = doc(db, 'contracts', fianza.contratoId);

    const unsubscribe = onSnapshot(contractDocRef, (contractDoc) => {
      if (contractDoc.exists()) {
        const contractData = contractDoc.data();
        const updatedFianza = contractData.fianzas?.find(f => f.id === fianza.id);
        if (updatedFianza) {
          setLiveFianzaData(updatedFianza);
        }
      }
    }, (error) => {
      console.error('Error listening to fianza updates:', error);
    });

    // Cleanup listener on unmount
    return () => unsubscribe();
  }, [isOpen, fianza?.contratoId, fianza?.id]);

  // Hide body scroll when view is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Reset states when closing
  const handleClose = () => {
    setReplacingFile(null);
    setShowConfirmDialog(false);
    setPendingUpload(null);
    setParseError(null);
    setSuccessMessage(null);
    onClose();
  };

  // Archivar = borrado lógico, never a delete. The backend flags the fianza and
  // its ledger movements together, reopens the requerida and writes the
  // bitácora; this view only opens the dialog and closes itself afterwards.
  const [isArchivarOpen, setIsArchivarOpen] = useState(false);
  const handleArchivada = () => {
    setIsArchivarOpen(false);
    if (onUpdate) onUpdate();
    handleClose();
  };

  // Handle opening file in new window
  const handleOpenFile = async (filePath) => {
    try {
      const fileRef = ref(storage, filePath);
      const url = await getDownloadURL(fileRef);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error getting file URL:', error);
      alert('Error al abrir el archivo');
    }
  };


  // Handle file replacement (with XML parsing for XML files)
  const handleReplaceFile = async (fileKey, newFiles) => {
    if (!newFiles || newFiles.length === 0) return;

    const file = newFiles[0];
    setParseError(null);
    setParseErrorKey(null);

    // For XML files: parse first
    if (fileKey === 'fianzaXml' || fileKey === 'facturaXml') {
      const parseResult = fileKey === 'fianzaXml'
        ? await parseXmlFianza(file)
        : await parseXmlFactura(file);

      if (!parseResult.success) {
        setParseError(parseResult.error);
        setParseErrorKey(fileKey);
        return;
      }

      // Run cross-validation if both XMLs will exist
      const existingFianzaData = fileKey === 'fianzaXml' ? parseResult.data : liveFianzaData.xmlFianza;
      const existingFacturaData = fileKey === 'facturaXml' ? parseResult.data : liveFianzaData.xmlFactura;

      const warnings = (existingFianzaData && existingFacturaData)
        ? validateFianzaFactura(existingFianzaData, existingFacturaData)
        : [];

      if (warnings.length > 0) {
        // Show confirmation dialog
        setPendingUpload({ fileKey, file, parsedData: parseResult.data, warnings });
        setShowConfirmDialog(true);
        return;
      }

      // No warnings - proceed directly
      await uploadAndSaveXml(fileKey, file, parseResult.data);
    } else {
      // Non-XML files - existing behavior (PDF, comprobante)
      await uploadNonXmlFile(fileKey, file);
    }
  };

  // Upload and save XML file with parsed data
  const uploadAndSaveXml = async (fileKey, file, parsedData) => {
    try {
      setIsUpdating(true);

      // Delete old file from storage if it exists
      if (liveFianzaData.filePaths && liveFianzaData.filePaths[fileKey]) {
        try {
          const oldFileRef = ref(storage, liveFianzaData.filePaths[fileKey]);
          await deleteObject(oldFileRef);
        } catch (error) {
          console.log('Old file not found or already deleted:', error);
        }
      }

      // Upload new file
      const basePath = fianzaStoragePath(contratoData, clientData?.id, fianza.id);
      const fileName = fileKey === 'fianzaXml' ? 'fianza.xml' : 'factura.xml';
      const newPath = `${basePath}/${fileName}`;

      const newFileRef = ref(storage, newPath);
      await uploadBytes(newFileRef, file);

      // Update fianza document with file path AND parsed data
      {
        await mutateFianza(db, fianza.contratoId, fianza.id, (f) => {
          const updates = {
            ...f,
            filePaths: { ...f.filePaths, [fileKey]: newPath },
            updatedAt: Date.now(),
          };

          // Update parsed XML data and root-level fields
          if (fileKey === 'fianzaXml') {
            updates.xmlFianza = parsedData;
            updates.numeroFianza = parsedData.numeroFianza;
            updates.numeroInclusion = parsedData.numeroInclusion || null;
            updates.montoAfianzado = parsedData.montoAfianzado;
            updates.tipoFianza = parsedData.tipo;
            updates.inicioVigenciaFianza = parsedData.inicioVigenciaFianza;
            updates.finVigenciaFianza = parsedData.finVigenciaFianza;
          } else if (fileKey === 'facturaXml') {
            updates.xmlFactura = parsedData;
            updates.monto = parsedData.total;
            // Update numeroFianza from noIdentificacion if not set
            if (!f.numeroFianza && parsedData.noIdentificacion) {
              updates.numeroFianza = parsedData.noIdentificacion;
            }
          }

          return updates;
        });

        // Log file addition or update with parsed data info
        const fileTypeLabel = fileKey === 'fianzaXml' ? 'XML Fianza' : 'XML Factura';
        const numeroFianza = parsedData.numeroFianza || parsedData.noIdentificacion || fianza.numeroFianza || 'sin numero';
        const hadPreviousFile = liveFianzaData.filePaths && liveFianzaData.filePaths[fileKey];
        const verb = hadPreviousFile ? 'Actualizo' : 'Agrego';

        try {
          await logBitacora({
            action: 'UPDATE',
            collection: 'contracts',
            documentId: fianza.contratoId,
            metadata: buildBitacoraMeta(
              `${verb} archivo '${fileTypeLabel}' de fianza #${numeroFianza} (datos extraidos y actualizados)`
            ),
            user,
            userData,
          });
        } catch (logError) {
          console.error('Failed to log file operation:', logError);
        }

        // Call onUpdate to refresh parent component
        if (onUpdate) await onUpdate();

        // Show success message
        setSuccessMessage('XML procesado correctamente. Datos actualizados.');
        setTimeout(() => setSuccessMessage(null), 3000);
      }

      setReplacingFile(null);
      setIsUpdating(false);
    } catch (error) {
      console.error('Error uploading XML file:', error);
      alert('Error al subir el archivo XML');
      setIsUpdating(false);
    }
  };

  // Upload non-XML files (PDF, comprobante)
  const uploadNonXmlFile = async (fileKey, file) => {
    try {
      setIsUpdating(true);

      // Delete old file from storage if it exists
      if (liveFianzaData.filePaths && liveFianzaData.filePaths[fileKey]) {
        try {
          const oldFileRef = ref(storage, liveFianzaData.filePaths[fileKey]);
          await deleteObject(oldFileRef);
        } catch (error) {
          console.log('Old file not found or already deleted:', error);
        }
      }

      // Upload new file
      const basePath = fianzaStoragePath(contratoData, clientData?.id, fianza.id);
      const fileName = fileKey === 'fianzaPdf' ? 'fianza.pdf'
                     : fileKey === 'facturaPdf' ? 'factura.pdf'
                     : fileKey === 'comprobantePago' ? `comprobante_pago.${file.name.split('.').pop()}`
                     : file.name;
      const newPath = `${basePath}/${fileName}`;

      const newFileRef = ref(storage, newPath);
      await uploadBytes(newFileRef, file);

      // Update fianza document
      {
        await mutateFianza(db, fianza.contratoId, fianza.id, (f) => {
          const updates = {
            ...f,
            filePaths: { ...f.filePaths, [fileKey]: newPath },
            updatedAt: Date.now(),
          };

          // If uploading comprobante de pago, automatically mark as paid
          if (fileKey === 'comprobantePago') {
            updates.pagada = true;
          }

          return updates;
        });

        // Log file addition or update (non-blocking)
        const fileTypeLabels = {
          fianzaPdf: 'PDF Fianza',
          facturaPdf: 'PDF Factura',
          comprobantePago: 'Comprobante de Pago',
        };

        const numeroFianza = liveFianzaData.numeroFianza || liveFianzaData.numeroPoliza || 'sin numero';
        const fileTypeLabel = fileTypeLabels[fileKey] || fileKey;

        // Check if file existed before (addition vs update)
        const hadPreviousFile = liveFianzaData.filePaths && liveFianzaData.filePaths[fileKey];
        const verb = hadPreviousFile ? 'Actualizo' : 'Agrego';

        try {
          await logBitacora({
            action: 'UPDATE',
            collection: 'contracts',
            documentId: fianza.contratoId,
            metadata: buildBitacoraMeta(
              `${verb} archivo '${fileTypeLabel}' de fianza #${numeroFianza}`
            ),
            user,
            userData,
          });
        } catch (logError) {
          console.error('Failed to log file operation:', logError);
        }

        // Call onUpdate to refresh parent component
        if (onUpdate) await onUpdate();
      }

      setReplacingFile(null);
      setIsUpdating(false);
    } catch (error) {
      console.error('Error replacing file:', error);
      alert('Error al reemplazar el archivo');
      setIsUpdating(false);
    }
  };

  // Confirm pending XML upload
  const confirmPendingUpload = async () => {
    if (!pendingUpload) return;
    const { fileKey, file, parsedData } = pendingUpload;
    setShowConfirmDialog(false);
    await uploadAndSaveXml(fileKey, file, parsedData);
    setPendingUpload(null);
  };

  // Cancel pending XML upload
  const cancelPendingUpload = () => {
    setShowConfirmDialog(false);
    setPendingUpload(null);
    setReplacingFile(null);
  };

  // Payment is tracked per factura (facturas[].pagada gates comisión → cobrada).
  // A read-modify-write that applies `updater` to this fianza's facturas and
  // keeps the legacy root `pagada` in sync (true only when every factura is paid).
  const numeroFianzaLabel = fianza.numeroFianza || fianza.numeroPoliza || 'sin número';

  const updateFacturasPagada = async ({ facturaIds, pagada }, description) => {
    try {
      setIsUpdating(true);
      // Through the backend: one transaction updates the contract's facturas,
      // the root pagada projection, AND the facturaPagada flag on the ledger
      // movements — so the Comisiones page stays contract-independent.
      await setFacturaPagada({
        contractId: fianza.contratoId,
        fianzaId: fianza.id,
        facturaIds,
        pagada,
      });
      try {
        await logBitacora({
          action: 'UPDATE',
          collection: 'contracts',
          documentId: fianza.contratoId,
          metadata: buildBitacoraMeta(description),
          user,
          userData,
        });
      } catch (logError) {
        console.error('Failed to log payment status change:', logError);
      }
      if (onUpdate) await onUpdate();
      setIsUpdating(false);
    } catch (error) {
      console.error('Error toggling pagada status:', error);
      alert('Error al actualizar el estado de pago');
      setIsUpdating(false);
    }
  };

  // Mark every factura paid/unpaid at once (single-factura case, or a bulk action).
  const markAllFacturasPagada = (next) => updateFacturasPagada(
    { facturaIds: null, pagada: next },
    `Marcó fianza #${numeroFianzaLabel} como ${next ? 'pagada' : 'pendiente de pago'}`
  );

  // Mark a single factura paid/unpaid (when the fianza has several facturas).
  const toggleFacturaPagada = (facturaId, next) => updateFacturasPagada(
    { facturaIds: [facturaId], pagada: next },
    `Marcó una factura como ${next ? 'pagada' : 'pendiente'} en fianza #${numeroFianzaLabel}`
  );

  // Toggle renovable status
  const handleToggleRenovable = async () => {
    try {
      setIsUpdating(true);

      const oldRenovableStatus = liveFianzaData.renovable || false;
      const newRenovableStatus = !oldRenovableStatus;

      {
        await mutateFianza(db, fianza.contratoId, fianza.id, (current) => ({
          ...current,
          renovable: newRenovableStatus,
          updatedAt: Date.now(),
        }));

        // Log to bitacora
        const numeroFianza = liveFianzaData.numeroFianza || liveFianzaData.xmlFianza?.noFianza || fianza.id;
        await logBitacora({
          action: 'UPDATE',
          collection: 'contracts',
          documentId: fianza.contratoId,
          changes: [{
            field: 'fianzas.renovable',
            oldValue: oldRenovableStatus,
            newValue: newRenovableStatus,
          }],
          metadata: buildBitacoraMeta(
            `Marcó fianza #${numeroFianza} como ${newRenovableStatus ? 'renovable' : 'no renovable'}`
          ),
          user,
          userData,
        });

        // Call onUpdate to refresh parent component
        if (onUpdate) await onUpdate();
      }

      setIsUpdating(false);
    } catch (error) {
      console.error('Error toggling renovable status:', error);
      alert('Error al actualizar el estado renovable');
      setIsUpdating(false);
    }
  };

  // Toggle the beneficiario-convenio marker: the backend flips the flag and
  // re-prices the fianza's pending comisión movements in one transaction.
  const handleToggleBeneficiarioConvenio = async () => {
    try {
      setIsUpdating(true);
      const nuevo = !liveFianzaData.beneficiarioConvenio;
      await setBeneficiarioConvenio({
        contractId: fianza.contratoId,
        fianzaId: fianza.id,
        beneficiarioConvenio: nuevo,
      });
      const numeroFianza = liveFianzaData.numeroFianza || liveFianzaData.xmlFianza?.noFianza || fianza.id;
      await logBitacora({
        action: 'UPDATE',
        collection: 'contracts',
        documentId: fianza.contratoId,
        changes: [{
          field: 'fianzas.beneficiarioConvenio',
          oldValue: !nuevo,
          newValue: nuevo,
        }],
        metadata: buildBitacoraMeta(
          `Marcó fianza #${numeroFianza} ${nuevo ? 'con' : 'sin'} convenio de proveedores del beneficiario`
        ),
        user,
        userData,
      });
      if (onUpdate) await onUpdate();
      setIsUpdating(false);
    } catch (error) {
      console.error('Error toggling beneficiario convenio:', error);
      alert(error.message || 'Error al actualizar el marcador de convenio');
      setIsUpdating(false);
    }
  };

  if (!isOpen || !fianza) return null;

  const fileTypes = [
    { key: 'fianzaXml', label: 'XML Fianza', type: 'xml', required: false },
    { key: 'facturaXml', label: 'XML Factura', type: 'xml', required: true },
    { key: 'fianzaPdf', label: 'PDF Fianza', type: 'pdf', required: false },
    { key: 'facturaPdf', label: 'PDF Factura', type: 'pdf', required: false },
  ];

  // Projections stored before the desglose fix in projectXmlFianza can carry a
  // single-factura breakdown (derechos, gastos, IVA…) next to a CUMULATIVE
  // prima. A prima larger than its own subtotal cannot belong to the same
  // factura, so the breakdown rows are hidden and only the prima shows.
  const primasXmlFianza = liveFianzaData.xmlFianza?.primas || null;
  const desgloseValido = !(
    primasXmlFianza?.subTotal != null &&
    primasXmlFianza?.prima != null &&
    primasXmlFianza.prima > primasXmlFianza.subTotal
  );

  // The root xmlFactura is always the LATEST factura (applyEndoso swaps it in),
  // so with more than one vigente the card needs to say which factura it shows
  // and what the fianza accumulates across all of them.
  const facturasVigentes = getFacturasVigentes(ensureFacturas(liveFianzaData));
  const ultimaFactura = facturasVigentes[facturasVigentes.length - 1] || null;
  const totalesFacturas = facturasVigentes.map((f) => Number(f?.xmlFactura?.total));
  const totalFacturado = totalesFacturas.length > 0 && totalesFacturas.every((n) => Number.isFinite(n))
    ? totalesFacturas.reduce((s, n) => s + n, 0)
    : null;

  // Every version of a document slot across the event history, newest first.
  // Endoso documents live on their evento's filePaths and never change; the
  // root filePaths is the LIVE emisión slot ("Cambiar" overwrites it and
  // deletes the previous storage object, so the emisión evento's own copy of
  // the path can go stale — always read the root for that version).
  const getDocVersiones = (key) => {
    const versiones = [];
    [...ensureEventos(liveFianzaData)]
      .sort((a, b) => (b.fecha || b.createdAt || 0) - (a.fecha || a.createdAt || 0))
      .forEach((ev) => {
        const path = ev.tipo === EVENTO_TIPOS.EMISION
          ? liveFianzaData.filePaths?.[key]
          : ev.filePaths?.[key];
        if (path) {
          versiones.push({
            path,
            label: formatEventoTipos(ev),
            fecha: ev.fecha || ev.createdAt || null,
            esEmision: ev.tipo === EVENTO_TIPOS.EMISION,
          });
        }
      });
    return versiones;
  };

  return (
    <div className="fixed inset-0 bg-white z-50 overflow-hidden">
      {/* Header */}
      <div className="bg-azul-surelia text-white px-6 py-4 flex items-center justify-between shadow-lg">
        <div className="flex items-center gap-4 flex-1">
          <Button
            color="white"
            onClick={handleClose}
            className="flex items-center gap-2"
            disabled={isUpdating}
          >
            <ArrowLeftIcon className="h-5 w-5" />
            Volver
          </Button>
          {EXPORT_ENABLED && (
            <Button
              color="white"
              onClick={handleExportView}
              disabled={isExportingView || isUpdating}
              className="flex items-center gap-2"
            >
              <CameraIcon className="h-5 w-5" />
              {isExportingView ? 'Exportando...' : 'Exportar'}
            </Button>
          )}
          {!isClient && (
            <>
              <Button
                color="white"
                onClick={() => setIsArchivarOpen(true)}
                disabled={isUpdating}
                className="flex items-center gap-2"
              >
                <ArchiveBoxIcon className="h-5 w-5" />
                Archivar
              </Button>
              <ArchivarFianzaDialog
                open={isArchivarOpen}
                onClose={() => setIsArchivarOpen(false)}
                contratoId={fianza?.contratoId}
                fianza={liveFianzaData}
                onArchived={handleArchivada}
              />
            </>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              {/* Número con la inclusión pegada (formatNumeroFianza), igual que
                  en las listas y en el ledger de comisiones. */}
              <h2 className="text-xl font-bold truncate">
                {formatNumeroFianza(liveFianzaData, 'Sin número')}
              </h2>
              {getEstadoFianza(liveFianzaData) !== ESTADO_FIANZA.ACTIVA && (
                <span className="px-2 py-0.5 bg-zinc-200 text-zinc-700 text-xs font-semibold rounded-full">
                  {ESTADO_LABELS[getEstadoFianza(liveFianzaData)] || getEstadoFianza(liveFianzaData)}
                </span>
              )}
              {liveFianzaData.renovable && (
                <span className="px-2 py-0.5 bg-verde-lima text-azul-surelia text-xs font-semibold rounded-full">
                  Renovable
                </span>
              )}
            </div>
            <p className="text-sm opacity-90">
              {liveFianzaData.tipoFianza || 'Tipo pendiente'}
            </p>
          </div>
        </div>
        {!isClient && clientData?.id && (
          <Button
            color="white"
            onClick={() => setIsNotifyOpen(true)}
            disabled={isUpdating}
            className="flex items-center gap-2 flex-shrink-0 ml-4"
          >
            <EnvelopeIcon className="h-5 w-5" />
            <span className="hidden sm:inline">Enviar por correo</span>
            <span className="sm:hidden">Enviar</span>
          </Button>
        )}
      </div>

      {/* Enviar fianza por correo */}
      <NotifyFianzaDialog
        open={isNotifyOpen}
        onClose={() => setIsNotifyOpen(false)}
        clientId={clientData?.id}
        clientName={clientData?.nombreEmpresa}
        contratoId={fianza?.contratoId}
        fianzaId={liveFianzaData?.id}
        numeroFianza={liveFianzaData?.numeroFianza}
        filePaths={liveFianzaData?.filePaths}
        eventos={liveFianzaData?.eventos}
        onSent={(sentTo) => {
          setSuccessMessage(`Correo enviado a ${sentTo.join(', ')}`);
          setTimeout(() => setSuccessMessage(null), 3000);
        }}
      />

      {/* Success Toast */}
      {successMessage && (
        <div className="fixed top-20 right-4 z-50 bg-green-600 text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 animate-fade-in">
          <CheckCircleIcon className="w-5 h-5" />
          <span className="text-sm font-medium">{successMessage}</span>
        </div>
      )}

      {/* Main content area */}
      <div ref={exportRef} className="h-[calc(100dvh-80px)] overflow-y-auto bg-zinc-50">
        <div className="max-w-7xl mx-auto p-4 sm:p-6">

          {/* Compromisos — an obligation still open, not an event that
              happened. Deliberately its own card ABOVE the historial: a
              compromiso is not an endoso and not history, and mixing them made
              "Registrar compromiso" read as a third endoso action. */}
          {!isClient && liveFianzaData?.id && (
            <div className="mb-4">
              <OtActivas fianzaId={liveFianzaData.id} titulo="Órdenes de trabajo de la fianza" onNavigate={onClose} />
            </div>
          )}
          {!isClient && clientData?.id && (
            <div className="mb-4">
              <PendientesPanel
                scope={{ fianzaId: liveFianzaData?.id || null }}
                correoFiado
                clientName={clientData?.nombreEmpresa || ''}
                titulo="Compromisos"
                nuevoLabel="Registrar compromiso"
                vacio="linea"
                tarjeta
                Icon={ShieldExclamationIcon}
                tipos={[PENDIENTE_TIPOS.COMPROMISO_AFIANZADORA]}
                preset={{
                  tipo: PENDIENTE_TIPOS.COMPROMISO_AFIANZADORA,
                  clienteId: clientData?.id || null,
                  contratoId: contratoData?.id || null,
                  fianzaId: liveFianzaData?.id || null,
                  contratoTitulo: contratoData?.tituloContrato?.value || '',
                  numeroFianza: liveFianzaData?.numeroFianza || '',
                  afianzadoraRaw: getEmisorNombre(liveFianzaData) || null,
                }}
              />
            </div>
          )}

          {/* Historial / Endosos */}
          <div className="mb-4 bg-white rounded-lg border border-gray-200 shadow-sm p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-800 text-sm flex items-center gap-2">
                <ArrowPathIcon className="w-4 h-4 text-azul-surelia" />
                Historial / Endosos
              </h3>
              {!isClient && (
                <div className="flex items-center gap-2">
                  {/* Renovar: renovable fianzas only. The old fianza keeps estado
                      and comisión; a new fianza entity is created for the next
                      period. Hidden once renewed (renovadaPor set). */}
                  {getEstadoFianza(liveFianzaData) === ESTADO_FIANZA.ACTIVA &&
                    liveFianzaData.renovable && !liveFianzaData.renovadaPor && (
                    <Button plain onClick={() => setShowRenovar(true)} disabled={isUpdating}>
                      Renovar
                    </Button>
                  )}
                  {getEstadoFianza(liveFianzaData) === ESTADO_FIANZA.ACTIVA && (
                    <Button plain onClick={() => setShowSustituir(true)} disabled={isUpdating}>
                      Sustituir
                    </Button>
                  )}
                  {/* A terminal fianza cannot be endorsed — the banner below
                      explains why the action is gone, same gate as Sustituir. */}
                  {getEstadoFianza(liveFianzaData) === ESTADO_FIANZA.ACTIVA && (
                    <>
                      <Button plain onClick={() => setShowSolicitar(true)} disabled={isUpdating}>
                        Solicitar endoso
                      </Button>
                      <Button
                        color="surelia"
                        onClick={() => { setEndosoRequeridoId(null); setShowEndoso(true); }}
                        disabled={isUpdating}
                      >
                        Registrar endoso
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>

            {getEstadoFianza(liveFianzaData) !== ESTADO_FIANZA.ACTIVA && (
              <div className="mb-3 px-3 py-2 rounded-md bg-zinc-100 border border-zinc-300 text-xs text-zinc-700">
                Esta fianza está{' '}
                <span className="font-semibold">
                  {getEstadoFianza(liveFianzaData) === ESTADO_FIANZA.ANULADA ? 'anulada' : 'cancelada'}
                </span>.
              </div>
            )}

            {endososAbiertos.length > 0 && (
              <div className="mb-3 space-y-2">
                {endososAbiertos.map((p) => {
                  const u = clasificarEndosoPendiente(p);
                  const tone = u.code === 'vencido'
                    ? 'border-red-300 bg-red-50 text-red-800'
                    : u.code === 'urgente'
                    ? 'border-amber-300 bg-amber-50 text-amber-800'
                    : 'border-dashed border-gray-300 bg-gray-50 text-gray-700';
                  return (
                    <div key={p.id} className={`rounded-lg border px-3 py-2 text-xs ${tone}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <span className="font-semibold">Pendiente: </span>
                          {p.tipos?.length
                            ? p.tipos.map((t) => EVENTO_TIPO_LABELS_LOCAL[t] || t).join(' + ')
                            : 'Endoso solicitado'}
                          {p.fechaLimite && (
                            <span className="ml-2 opacity-80">
                              · límite {new Date(p.fechaLimite).toLocaleDateString('es-MX')}
                              {u.dias != null && (u.dias < 0
                                ? ` (${Math.abs(u.dias)} días de retraso)`
                                : ` (en ${u.dias} días)`)}
                            </span>
                          )}
                          {p.descripcion && <div className="mt-0.5 italic opacity-90">{p.descripcion}</div>}
                        </div>
                        {!isClient && (
                          <div className="shrink-0 flex items-center gap-1">
                            <Button
                              plain
                              onClick={() => { setEndosoRequeridoId(p.id); setShowEndoso(true); }}
                              disabled={isUpdating}
                            >
                              Registrar
                            </Button>
                            <button
                              type="button"
                              title="Cancelar solicitud"
                              disabled={isUpdating}
                              onClick={() => cancelarSolicitudEndoso(p)}
                              className="p-1 rounded text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600"
                            >
                              <XMarkIcon className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <ol className="relative border-l border-gray-200 ml-2 space-y-4">
              {[...ensureEventos(liveFianzaData)]
                .sort((a, b) => (b.fecha || b.createdAt || 0) - (a.fecha || a.createdAt || 0))
                .map((ev) => (
                  <li key={ev.id} className="ml-4">
                    <div className="absolute -left-1.5 mt-1 w-3 h-3 rounded-full bg-azul-surelia border-2 border-white" />
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-sm font-semibold text-gray-800">
                        {formatEventoTipos(ev)}
                      </div>
                      <div className="text-xs text-gray-400">
                        {ev.fecha ? new Date(ev.fecha).toLocaleDateString('es-MX') : ''}
                      </div>
                    </div>
                    {ev.cambios && ev.cambios.length > 0 && (
                      <ul className="mt-1 space-y-0.5">
                        {ev.cambios.map((c) => (
                          <li key={c.campo} className="text-xs text-gray-600 flex flex-wrap items-center gap-1">
                            <span className="font-medium">{c.label}:</span>
                            <span className="text-gray-400 line-through">{formatCambioValor(c.campo, c.valorAnterior)}</span>
                            <span className="text-gray-300">→</span>
                            <span className="text-gray-800">{formatCambioValor(c.campo, c.valorNuevo)}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {ev.nota && <div className="mt-1 text-xs text-gray-500 italic">{ev.nota}</div>}
                    {ev.filePaths && Object.keys(ev.filePaths).length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-2">
                        {Object.entries(ev.filePaths).map(([k, path]) => (
                          <button
                            key={k}
                            type="button"
                            onClick={() => handleOpenFile(path)}
                            className="text-xs text-cian-digital hover:underline"
                          >
                            {EVENT_FILE_LABELS[k] || k}
                          </button>
                        ))}
                      </div>
                    )}
                  </li>
                ))}
            </ol>
          </div>

          {/* Three-column layout on desktop */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">

            {/* LEFT COLUMN - Administrative: Client, Payment, Documents, Contract */}
            <div className="space-y-4">

              {/* Cliente / Beneficiario+Fiado Info - Compact */}
              <div className={`p-3 bg-white rounded-lg border-l-4 border border-gray-200 shadow-sm ${
                isConvenioContract ? 'border-l-[#53B5CC]' : 'border-l-azul-surelia'
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-gray-800 flex items-center gap-2 text-sm">
                    <UserCircleIcon className={`w-4 h-4 ${isConvenioContract ? 'text-[#53B5CC]' : 'text-azul-surelia'}`} />
                    {isConvenioContract ? 'Beneficiario y Fiado' : 'Cliente'}
                  </h3>
                  {!isConvenioContract && clientData?.id && (
                    <button
                      type="button"
                      onClick={() => { onClose?.(); navigate(`/app/clientes/${clientData.id}`); }}
                      className="text-xs font-medium text-azul-surelia hover:underline"
                    >
                      Ver cliente
                    </button>
                  )}
                </div>
                {isConvenioContract ? (
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
                    <div>
                      <div className="text-gray-500">Beneficiario</div>
                      <div className="text-gray-800 font-medium truncate">{beneficiario?.nombre || 'N/A'}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">RFC Beneficiario</div>
                      <div className="text-gray-800 font-mono">{beneficiario?.rfc || 'N/A'}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Fiado</div>
                      <div className="text-gray-800 font-medium truncate">{fiado || 'N/A'}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Convenio</div>
                      <div className="text-gray-800 truncate">{convenio?.nombre || 'N/A'}</div>
                    </div>
                    {proyectoNombre && (
                      <div className="col-span-2">
                        <div className="text-gray-500">Proyecto</div>
                        <div className="text-gray-800">{proyectoNombre}</div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
                    <div>
                      <div className="text-gray-500">Empresa</div>
                      <div className="text-gray-800 font-medium truncate">{clientData?.nombreEmpresa || 'N/A'}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Contacto</div>
                      <div className="text-gray-800">{clientData?.nombreContacto || 'N/A'}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Teléfono</div>
                      <div className="text-gray-800">{clientData?.telefonoContacto || 'N/A'}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Email</div>
                      <div className="text-gray-800 truncate">{clientData?.correoContacto || 'N/A'}</div>
                    </div>
                  </div>
                )}
              </div>

              {/* PAYMENT STATUS + Comprobante de Pago */}
              {(() => {
                const facturasPago = ensureFacturas(liveFianzaData);
                const paidCount = facturasPago.filter((fc) => fc.pagada).length;
                const allPaid = facturasPago.length > 0 && paidCount === facturasPago.length;
                const somePaid = paidCount > 0 && !allPaid;
                const multi = facturasPago.length > 1;
                const border = allPaid ? 'border-l-green-600' : somePaid ? 'border-l-amber-500' : 'border-l-orange-600';
                const iconBg = allPaid ? 'bg-green-100' : somePaid ? 'bg-amber-100' : 'bg-orange-100';
                const iconText = allPaid ? 'text-green-600' : somePaid ? 'text-amber-600' : 'text-orange-600';
                const label = allPaid ? 'Pagada' : somePaid ? `Pago parcial (${paidCount}/${facturasPago.length})` : 'Pendiente Pago';
                const labelText = allPaid ? 'text-green-700' : somePaid ? 'text-amber-700' : 'text-orange-700';
                return (
                <div className={`bg-white rounded-lg shadow-sm p-3 border-l-4 ${border}`}>
                {/* Status row */}
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${iconBg}`}>
                      <BanknotesIcon className={`w-4 h-4 ${iconText}`} />
                    </div>
                    <div className="min-w-0">
                      <span className={`text-sm font-semibold ${labelText}`}>{label}</span>
                    </div>
                  </div>
                  {!isClient && (
                    multi ? (
                      <Button
                        color="zinc"
                        onClick={() => setShowFacturasPago((v) => !v)}
                        disabled={isUpdating}
                        className="px-3 py-1.5 text-xs flex-shrink-0"
                      >
                        {showFacturasPago ? 'Ocultar facturas' : `Ver facturas (${facturasPago.length})`}
                      </Button>
                    ) : (
                      <Button
                        color={allPaid ? "zinc" : "surelia"}
                        onClick={() => markAllFacturasPagada(!allPaid)}
                        disabled={isUpdating}
                        className="px-3 py-1.5 text-xs flex-shrink-0"
                      >
                        {allPaid ? 'Marcar Pendiente' : 'Marcar Pagada'}
                      </Button>
                    )
                  )}
                </div>

                {/* Per-factura payment list (only when there are several facturas) */}
                {multi && showFacturasPago && (
                  <div className="mt-3 pt-3 border-t border-gray-200 space-y-1.5">
                    {!isClient && (
                      <div className="flex justify-end gap-2 mb-1 text-[11px]">
                        <button type="button" onClick={() => markAllFacturasPagada(true)} disabled={isUpdating} className="text-green-700 hover:underline">Todas pagadas</button>
                        <span className="text-gray-300">·</span>
                        <button type="button" onClick={() => markAllFacturasPagada(false)} disabled={isUpdating} className="text-orange-700 hover:underline">Todas pendientes</button>
                      </div>
                    )}
                    {facturasPago.map((fc) => (
                      <div key={fc.id} className="flex items-center justify-between gap-2 rounded-lg border border-gray-200 px-3 py-2">
                        <div className="min-w-0 text-xs">
                          <span className="font-medium text-gray-800">{FACTURA_MODE_LABELS[fc.mode] || fc.mode}</span>
                          <span className="ml-2 text-gray-500">
                            {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(Number(fc.prima) || 0)}
                          </span>
                          {fc.facturaId && <span className="ml-2 text-gray-400">#{fc.facturaId}</span>}
                        </div>
                        {!isClient ? (
                          <Button
                            color={fc.pagada ? "zinc" : "surelia"}
                            onClick={() => toggleFacturaPagada(fc.id, !fc.pagada)}
                            disabled={isUpdating}
                            className="px-2.5 py-1 text-[11px] flex-shrink-0"
                          >
                            {fc.pagada ? 'Pagada' : 'Marcar pagada'}
                          </Button>
                        ) : (
                          <span className={`text-[11px] ${fc.pagada ? 'text-green-700' : 'text-orange-700'}`}>
                            {fc.pagada ? 'Pagada' : 'Pendiente'}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Comprobante de Pago */}
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-medium text-gray-600">Comprobante:</span>
                    {(() => {
                      const hasComprobante = liveFianzaData.filePaths && liveFianzaData.filePaths.comprobantePago;
                      const isReplacing = replacingFile === 'comprobantePago';

                      if (hasComprobante && !isReplacing) {
                        return (
                          <div className="flex items-center gap-2">
                            <CheckCircleIcon className="w-4 h-4 text-green-600" />
                            <span className="text-xs text-green-700 font-medium">Cargado</span>
                            <button
                              onClick={() => handleOpenFile(liveFianzaData.filePaths.comprobantePago)}
                              className="bg-azul-surelia hover:bg-azul-surelia/90 text-white rounded px-2 py-1 text-xs font-medium transition-all"
                            >
                              Ver
                            </button>
                            {!isClient && (
                              <button
                                onClick={() => setReplacingFile('comprobantePago')}
                                className="px-2 py-1 rounded text-xs font-medium bg-gray-200 hover:bg-gray-300 text-gray-700 transition-all"
                              >
                                Cambiar
                              </button>
                            )}
                          </div>
                        );
                      }

                      if (isClient) {
                        return <span className="text-xs text-gray-400">Sin comprobante</span>;
                      }

                      return (
                        <div className="flex items-center gap-2">
                          <CompactFileReceiver
                            onFilesChange={(files) => handleReplaceFile('comprobantePago', files)}
                            maxFiles={1}
                            type="image-or-pdf"
                            disabled={isUpdating}
                            placeholder="Subir comprobante"
                            variant="inline"
                          />
                          {isReplacing && (
                            <button
                              onClick={() => setReplacingFile(null)}
                              className="text-[10px] text-azul-surelia hover:underline"
                            >
                              Cancelar
                            </button>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
                );
              })()}

              {/* RENOVABLE STATUS */}
              <div className={`bg-white rounded-lg shadow-sm p-3 border-l-4 ${
                liveFianzaData.renovable ? 'border-l-verde-lima' : 'border-l-gray-400'
              }`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      liveFianzaData.renovable ? 'bg-verde-lima/20' : 'bg-gray-100'
                    }`}>
                      <ArrowPathIcon className={`w-4 h-4 ${
                        liveFianzaData.renovable ? 'text-azul-surelia' : 'text-gray-500'
                      }`} />
                    </div>
                    <div className="min-w-0">
                      <span className={`text-sm font-semibold ${
                        liveFianzaData.renovable ? 'text-azul-surelia' : 'text-gray-600'
                      }`}>
                        {liveFianzaData.renovable ? 'Renovable' : 'No Renovable'}
                      </span>
                    </div>
                  </div>
                  {!isClient && (
                    <Button
                      color={liveFianzaData.renovable ? "zinc" : "surelia"}
                      onClick={handleToggleRenovable}
                      disabled={isUpdating}
                      className="px-3 py-1.5 text-xs flex-shrink-0"
                    >
                      {liveFianzaData.renovable ? 'Marcar No Renovable' : 'Marcar Renovable'}
                    </Button>
                  )}
                </div>
              </div>

              {/* BENEFICIARIO CON CONVENIO — nets the fiado share of the
                  comisión. Not shown for convenio contracts (already split). */}
              {!contratoData?.convenioId && !isClient && (() => {
                const marcadoContrato = Boolean(contratoData?.beneficiarioConvenio);
                const marcado = Boolean(liveFianzaData.beneficiarioConvenio) || marcadoContrato;
                return (
                  <div className={`bg-white rounded-lg shadow-sm p-3 border-l-4 ${
                    marcado ? 'border-l-azul-surelia' : 'border-l-gray-400'
                  }`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          marcado ? 'bg-azul-surelia/10' : 'bg-gray-100'
                        }`}>
                          <BanknotesIcon className={`w-4 h-4 ${
                            marcado ? 'text-azul-surelia' : 'text-gray-500'
                          }`} />
                        </div>
                        <div className="min-w-0">
                          <span className={`text-sm font-semibold ${
                            marcado ? 'text-azul-surelia' : 'text-gray-600'
                          }`}>
                            {marcado ? 'Beneficiario con convenio' : 'Sin convenio del beneficiario'}
                          </span>
                          {marcadoContrato && (
                            <div className="text-[11px] text-gray-400">Marcado a nivel contrato</div>
                          )}
                        </div>
                      </div>
                      {userData?.rol === 'owner' && !marcadoContrato && (
                        <Button
                          color={liveFianzaData.beneficiarioConvenio ? "zinc" : "surelia"}
                          onClick={handleToggleBeneficiarioConvenio}
                          disabled={isUpdating}
                          className="px-3 py-1.5 text-xs flex-shrink-0"
                          title="Recalcula las comisiones pendientes de esta fianza"
                        >
                          {liveFianzaData.beneficiarioConvenio ? 'Quitar convenio' : 'Marcar convenio'}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Documents Section - Compact 2x2 grid */}
              <div className="bg-white border-l-4 border-azul-surelia rounded-lg shadow-sm p-3">
                <h3 className="text-azul-surelia font-semibold text-sm mb-2">Documentos</h3>
                <div className="grid grid-cols-2 gap-2">
                  {fileTypes.map(({ key, label, type, required }) => {
                    const versiones = getDocVersiones(key);
                    const current = versiones[0] || null;
                    const anteriores = versiones.slice(1);
                    const isReplacing = replacingFile === key;

                    return (
                      <div key={key} className="bg-gray-50 border border-gray-200 rounded p-2">
                        <div className="flex items-center gap-1 mb-1.5">
                          <DocumentTextIcon className="w-3 h-3 text-azul-surelia flex-shrink-0" />
                          <div className="font-medium text-gray-800 text-xs truncate">
                            {label} {required && <span className="text-red-500">*</span>}
                          </div>
                        </div>

                        {current && !isReplacing ? (
                          <div className="space-y-1.5">
                            <div className="flex items-center gap-1 text-xs text-green-700 bg-green-50 rounded px-1.5 py-0.5">
                              <CheckCircleIcon className="w-3 h-3" />
                              <span className="font-medium">Cargado</span>
                              {!current.esEmision && (
                                <span className="ml-auto text-[10px] truncate" title={current.label}>
                                  {current.label}
                                </span>
                              )}
                            </div>
                            <div className="flex gap-1">
                              <button
                                onClick={() => handleOpenFile(current.path)}
                                className="flex-1 bg-azul-surelia hover:bg-azul-surelia/90 text-white rounded px-2 py-1 text-xs font-medium transition-all"
                              >
                                Ver
                              </button>
                              {/* Replacing the root slot only makes sense while the
                                  emisión doc is the current one: for XML slots it
                                  re-projects root data, which would clobber values
                                  an endoso already moved forward. */}
                              {!isClient && current.esEmision && (
                                <button
                                  onClick={() => setReplacingFile(key)}
                                  className="px-2 py-1 rounded text-xs font-medium bg-gray-200 hover:bg-gray-300 text-gray-700 transition-all"
                                >
                                  Cambiar
                                </button>
                              )}
                            </div>
                            {anteriores.length > 0 && (
                              <div className="pt-1 border-t border-gray-200 space-y-0.5">
                                {anteriores.map((v) => (
                                  <button
                                    key={v.path}
                                    type="button"
                                    onClick={() => handleOpenFile(v.path)}
                                    className="block w-full text-left text-[10px] text-cian-digital hover:underline truncate"
                                  >
                                    {v.label}
                                    {v.fecha ? ` · ${new Date(v.fecha).toLocaleDateString('es-MX')}` : ''}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        ) : isClient ? (
                          <div className="text-xs text-gray-400 py-1">Sin archivo</div>
                        ) : (
                          <div>
                            <CompactFileReceiver
                              onFilesChange={(files) => handleReplaceFile(key, files)}
                              maxFiles={1}
                              type={type}
                              disabled={isUpdating}
                            />
                            {isReplacing && (
                              <button
                                onClick={() => {
                                  setReplacingFile(null);
                                  setParseError(null);
                                  setParseErrorKey(null);
                                }}
                                className="mt-1 text-[10px] text-azul-surelia hover:underline"
                              >
                                Cancelar
                              </button>
                            )}
                            {/* Show parse error for XML files */}
                            {parseError && parseErrorKey === key && (key === 'fianzaXml' || key === 'facturaXml') && (
                              <div className="mt-1 text-xs text-red-600 bg-red-50 rounded px-2 py-1">
                                {parseError}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

            </div>

            {/* MIDDLE COLUMN - Factura Data */}
            <div className="space-y-4">
              {liveFianzaData.xmlFactura ? (
                <div className="p-3 bg-white rounded-lg shadow-sm border-l-4 border-l-amber-600 border border-gray-200">
                  <h3 className="font-semibold text-gray-800 mb-2 flex items-center gap-2 text-sm">
                    <svg className="w-4 h-4 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2zM10 8.5a.5.5 0 11-1 0 .5.5 0 011 0zm5 5a.5.5 0 11-1 0 .5.5 0 011 0z" />
                    </svg>
                    Datos de Factura
                    {facturasVigentes.length > 1 && (
                      <span className="ml-auto text-[10px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                        {FACTURA_MODE_LABELS[ultimaFactura?.mode] || 'Endoso'}
                      </span>
                    )}
                  </h3>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
                    <div>
                      <div className="text-gray-500">Serie-Folio</div>
                      <div className="text-gray-800 font-semibold">{liveFianzaData.xmlFactura.serie}-{liveFianzaData.xmlFactura.folio}</div>
                    </div>
                    {liveFianzaData.xmlFactura.fecha && (
                      <div>
                        <div className="text-gray-500">Fecha</div>
                        <div className="text-gray-800">{new Date(liveFianzaData.xmlFactura.fecha).toLocaleDateString('es-MX')}</div>
                      </div>
                    )}
                    <div>
                      <div className="text-gray-500">Subtotal</div>
                      <div className="text-gray-800 font-medium">
                        {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(liveFianzaData.xmlFactura.subTotal)}
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-500">IVA ({liveFianzaData.xmlFactura.tasaIVA}%)</div>
                      <div className="text-gray-800">
                        {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(liveFianzaData.xmlFactura.totalImpuestos)}
                      </div>
                    </div>
                    <div className="col-span-2 bg-amber-50 rounded p-1.5 -mx-1.5">
                      <div className="text-amber-700 font-medium">Total</div>
                      <div className="text-amber-800 font-bold text-sm">
                        {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(liveFianzaData.xmlFactura.total)}
                      </div>
                    </div>
                    {liveFianzaData.xmlFactura.fechaTimbrado && (
                      <div className="col-span-2">
                        <div className="text-gray-500">Fecha Timbrado</div>
                        <div className="text-gray-800">{new Date(liveFianzaData.xmlFactura.fechaTimbrado).toLocaleDateString('es-MX', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}</div>
                      </div>
                    )}
                    <div className="col-span-2">
                      <div className="text-gray-500">Folio Fiscal</div>
                      <div className="text-gray-700 font-mono text-[10px] break-all bg-gray-50 px-1.5 py-1 rounded border border-gray-200">{liveFianzaData.xmlFactura.folioFiscal}</div>
                    </div>
                    <div className="col-span-2 pt-1.5 mt-1.5 border-t border-gray-100">
                      <div className="text-gray-500">Emisor</div>
                      <div className="text-gray-800">{liveFianzaData.xmlFactura.emisor?.nombre || 'N/A'}</div>
                      {liveFianzaData.xmlFactura.emisor?.rfc && (
                        <div className="text-gray-400">RFC: {liveFianzaData.xmlFactura.emisor.rfc}</div>
                      )}
                    </div>
                    <div className="col-span-2">
                      <div className="text-gray-500">Receptor</div>
                      <div className="text-gray-800">{liveFianzaData.xmlFactura.receptor?.nombre || 'N/A'}</div>
                      {liveFianzaData.xmlFactura.receptor?.rfc && (
                        <div className="text-gray-400">RFC: {liveFianzaData.xmlFactura.receptor.rfc}</div>
                      )}
                    </div>
                    {liveFianzaData.xmlFactura.noIdentificacion && (
                      <div>
                        <div className="text-gray-500">No. Identificaci&oacute;n</div>
                        <div className="text-gray-800">{liveFianzaData.xmlFactura.noIdentificacion}</div>
                      </div>
                    )}
                    {liveFianzaData.xmlFactura.primas?.prima != null && (
                      <div>
                        <div className="text-gray-500">Prima</div>
                        <div className="text-gray-800 font-medium">
                          {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(liveFianzaData.xmlFactura.primas.prima)}
                        </div>
                      </div>
                    )}
                    {facturasVigentes.length > 1 && (
                      <>
                        <div className="col-span-2 mt-2 pt-2 border-t border-gray-200">
                          <div className="text-gray-700 font-semibold mb-1">
                            Acumulado ({facturasVigentes.length} facturas)
                          </div>
                        </div>
                        <div>
                          <div className="text-gray-500">Prima neta</div>
                          <div className="text-gray-800 font-semibold">
                            {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(getValidPrima(liveFianzaData))}
                          </div>
                        </div>
                        {totalFacturado != null && (
                          <div>
                            <div className="text-gray-500">Total facturado</div>
                            <div className="text-gray-800 font-semibold">
                              {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(totalFacturado)}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <div className="p-3 bg-white rounded-lg shadow-sm border-l-4 border-l-amber-400 border border-gray-200">
                  <h3 className="font-semibold text-gray-800 mb-2 flex items-center gap-2 text-sm">
                    <svg className="w-4 h-4 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2zM10 8.5a.5.5 0 11-1 0 .5.5 0 011 0zm5 5a.5.5 0 11-1 0 .5.5 0 011 0z" />
                    </svg>
                    Datos de Factura
                  </h3>
                  <div className="text-center py-4">
                    <p className="text-gray-500 text-xs">XML de factura no cargado</p>
                  </div>
                </div>
              )}

              {/* Contrato Info - Compact */}
              <div className="p-3 bg-white rounded-lg border-l-4 border-l-purple-600 border border-gray-200 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-gray-800 flex items-center gap-2 text-sm">
                    <DocumentCheckIcon className="w-4 h-4 text-purple-600" />
                    Contrato
                  </h3>
                  {contratoData?.id && (
                    <button
                      type="button"
                      onClick={() => setShowContrato(true)}
                      className="text-xs font-medium text-azul-surelia hover:underline"
                    >
                      Ver contrato
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
                  {contratoData?.numeroContrato && (
                    <div className="col-span-2">
                      <div className="text-gray-500">No. de Contrato</div>
                      <div className="text-gray-800 font-medium">
                        {typeof contratoData.numeroContrato === 'object'
                          ? (contratoData.numeroContrato.value || 'N/A')
                          : contratoData.numeroContrato}
                      </div>
                    </div>
                  )}
                  {contratoData?.contratante && (
                    <div className="col-span-2">
                      <div className="text-gray-500">Contratante</div>
                      <div className="text-gray-800 font-medium">
                        {typeof contratoData.contratante === 'object'
                          ? (contratoData.contratante.value || 'N/A')
                          : contratoData.contratante}
                      </div>
                    </div>
                  )}
                  {contratoData?.montoContrato && (
                    <div>
                      <div className="text-gray-500">Monto</div>
                      <div className="text-gray-800 font-medium">
                        {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(
                          typeof contratoData.montoContrato === 'object'
                            ? (contratoData.montoContrato.value || 0)
                            : contratoData.montoContrato
                        )}
                      </div>
                    </div>
                  )}
                  {contratoData?.fechaInicio && (
                    <div className="col-span-2">
                      <div className="text-gray-500">Vigencia</div>
                      <div className="text-gray-800">
                        {(() => {
                          const fmt = (v) => {
                            const d = new Date(typeof v === 'object' ? v?.value : v);
                            return Number.isFinite(d.getTime()) ? d.toLocaleDateString('es-MX') : null;
                          };
                          const inicio = fmt(contratoData.fechaInicio);
                          const fin = fmt(contratoData.fechaTermino);
                          return fin ? `${inicio || '—'} - ${fin}` : inicio || '—';
                        })()}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN - Fianza Data */}
            <div className="space-y-4">

              {/* Fianza Data */}
              <div className={`p-3 bg-white rounded-lg shadow-sm border-l-4 ${liveFianzaData.xmlFianza ? 'border-l-emerald-600' : 'border-l-orange-400'} border border-gray-200`}>
                <h3 className="font-semibold text-gray-800 mb-2 flex items-center gap-2 text-sm">
                  <svg className={`w-4 h-4 ${liveFianzaData.xmlFianza ? 'text-emerald-600' : 'text-orange-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  Datos de Fianza
                  {!liveFianzaData.xmlFianza && (
                    <span className="ml-2 text-xs font-medium text-orange-600 bg-orange-100 px-2 py-0.5 rounded">
                      Pendiente XML
                    </span>
                  )}
                </h3>
                {liveFianzaData.xmlFianza ? (
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
                    <div>
                      <div className="text-gray-500">Número</div>
                      <div className="text-gray-800 font-semibold">{liveFianzaData.xmlFianza.numeroFianza}</div>
                    </div>
                    {(liveFianzaData.numeroInclusion || liveFianzaData.xmlFianza.numeroInclusion) && (
                      <div>
                        <div className="text-gray-500">Inclusión</div>
                        <div className="text-gray-800 font-semibold">{liveFianzaData.numeroInclusion || liveFianzaData.xmlFianza.numeroInclusion}</div>
                      </div>
                    )}
                    {hasTarifa && (
                      <div>
                        <div className="text-gray-500">Tarifa</div>
                        <div className="text-gray-800">{liveFianzaData.xmlFianza.tarifa?.toFixed(4)}%</div>
                      </div>
                    )}
                    <div className="col-span-2">
                      <div className="text-gray-500">Monto Afianzado</div>
                      <div className="text-gray-800 font-semibold text-sm">
                        {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(liveFianzaData.xmlFianza.montoAfianzado)}
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-500">Ramo</div>
                      <div className="text-gray-800">{liveFianzaData.xmlFianza.ramo || 'N/A'}</div>
                    </div>
                    {liveFianzaData.xmlFianza.subRamo && (
                      <div>
                        <div className="text-gray-500">SubRamo</div>
                        <div className="text-gray-800">{liveFianzaData.xmlFianza.subRamo}</div>
                      </div>
                    )}
                    {liveFianzaData.xmlFianza.tipo && (
                      <div>
                        <div className="text-gray-500">Tipo</div>
                        <div className="text-gray-800">{liveFianzaData.xmlFianza.tipo}</div>
                      </div>
                    )}
                    <div>
                      <div className="text-gray-500">Vigencia Inicio</div>
                      <div className="text-gray-800">{new Date(liveFianzaData.inicioVigenciaFianza).toLocaleDateString('es-MX')}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Vigencia Fin</div>
                      <div className="text-gray-800">{new Date(liveFianzaData.finVigenciaFianza).toLocaleDateString('es-MX')}</div>
                    </div>
                    {Number.isFinite(liveFianzaData.xmlFianza.fechaSolicitada) && (
                      <div>
                        <div className="text-gray-500">Fecha Solicitada</div>
                        <div className="text-gray-800">{new Date(liveFianzaData.xmlFianza.fechaSolicitada).toLocaleDateString('es-MX')}</div>
                      </div>
                    )}
                    <div className="col-span-2 pt-1.5 mt-1.5 border-t border-gray-100">
                      <div className="text-gray-500">Emisor</div>
                      <div className="text-gray-800">{liveFianzaData.xmlFianza.emisor?.nombre || 'N/A'}</div>
                      {liveFianzaData.xmlFianza.emisor?.rfc && (
                        <div className="text-gray-400">RFC: {liveFianzaData.xmlFianza.emisor.rfc}</div>
                      )}
                    </div>
                    <div className="col-span-2">
                      <div className="text-gray-500">Fiado</div>
                      <div className="text-gray-800">{liveFianzaData.xmlFianza.fiado?.nombre || 'N/A'}</div>
                      {liveFianzaData.xmlFianza.fiado?.rfc && (
                        <div className="text-gray-400">RFC: {liveFianzaData.xmlFianza.fiado.rfc}</div>
                      )}
                      {contratoData?.proveedor?.value &&
                        liveFianzaData.xmlFianza.fiado?.nombre &&
                        !namesMatch(contratoData.proveedor.value, liveFianzaData.xmlFianza.fiado.nombre) && (
                          <div className="mt-2 px-2.5 py-1.5 bg-amber-50 border border-amber-300 rounded text-xs text-amber-800">
                            <span className="font-semibold">No coincide con el proveedor del contrato:</span>{' '}
                            <span className="font-medium">{contratoData.proveedor.value}</span>
                            <div className="text-[11px] text-amber-700 mt-0.5">
                              Se conserva el dato del contrato como fuente de verdad.
                            </div>
                          </div>
                        )}
                      {(() => {
                        // Cliente identity check — only for fianzas on cliente
                        // contracts (in the convenio flow the fiado is the
                        // proveedor, a different entity than the cliente).
                        if (convenio || contratoData?.convenioId) return null;
                        const fiado = liveFianzaData.xmlFianza.fiado;
                        const nameMm = clientData?.nombreEmpresa && fiado?.nombre && !namesMatch(clientData.nombreEmpresa, fiado.nombre);
                        const rfcMm = clientData?.rfc && fiado?.rfc && !rfcsMatch(clientData.rfc, fiado.rfc);
                        if (!nameMm && !rfcMm) return null;
                        return (
                          <div className="mt-2 px-2.5 py-1.5 bg-amber-50 border border-amber-300 rounded text-xs text-amber-800">
                            <span className="font-semibold">No coincide con el cliente</span>
                            {nameMm && (
                              <div className="text-[11px] text-amber-700 mt-0.5">
                                Cliente: <span className="font-medium">{clientData.nombreEmpresa}</span>
                              </div>
                            )}
                            {rfcMm && (
                              <div className="text-[11px] text-amber-700 mt-0.5">
                                RFC del cliente: <span className="font-medium">{clientData.rfc}</span>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                    <div className="col-span-2">
                      <div className="text-gray-500">Beneficiario</div>
                      <div className="text-gray-800">{liveFianzaData.xmlFianza.beneficiario?.nombre || 'N/A'}</div>
                    </div>
                    {liveFianzaData.xmlFianza.agente?.nombre && (
                      <div className="col-span-2">
                        <div className="text-gray-500">Agente</div>
                        <div className="text-gray-800">{liveFianzaData.xmlFianza.agente.nombre}</div>
                        {liveFianzaData.xmlFianza.agente.rfc && (
                          <div className="text-gray-400">RFC: {liveFianzaData.xmlFianza.agente.rfc}</div>
                        )}
                      </div>
                    )}
                    {(liveFianzaData.xmlFianza.folio || liveFianzaData.xmlFianza.idPoliza) && (
                      <>
                        {liveFianzaData.xmlFianza.folio && (
                          <div>
                            <div className="text-gray-500">Folio</div>
                            <div className="text-gray-800">{liveFianzaData.xmlFianza.folio}</div>
                          </div>
                        )}
                        {liveFianzaData.xmlFianza.idPoliza && (
                          <div>
                            <div className="text-gray-500">ID Póliza</div>
                            <div className="text-gray-800">{liveFianzaData.xmlFianza.idPoliza}</div>
                          </div>
                        )}
                      </>
                    )}
                    {liveFianzaData.xmlFianza.numeroCompaniaAfianzadora && (
                      <div className="col-span-2">
                        <div className="text-gray-500">No. Cía Afianzadora</div>
                        <div className="text-gray-800">{liveFianzaData.xmlFianza.numeroCompaniaAfianzadora}</div>
                      </div>
                    )}
                    {Number.isFinite(liveFianzaData.xmlFianza.fechaAutorizacion) && (
                      <div>
                        <div className="text-gray-500">Fecha Autorización</div>
                        <div className="text-gray-800">{new Date(liveFianzaData.xmlFianza.fechaAutorizacion).toLocaleDateString('es-MX')}</div>
                      </div>
                    )}

                    {/* Primas Section - Compact */}
                    {liveFianzaData.xmlFianza.primas && (
                      <>
                        <div className="col-span-2 mt-2 pt-2 border-t border-gray-200">
                          <div className="text-gray-700 font-semibold mb-1">Desglose de Primas</div>
                        </div>
                        {liveFianzaData.xmlFianza.primas.prima !== undefined && (
                          <div>
                            <div className="text-gray-500">Prima</div>
                            <div className="text-gray-800 font-medium">
                              {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(liveFianzaData.xmlFianza.primas.prima)}
                            </div>
                          </div>
                        )}
                        {desgloseValido && liveFianzaData.xmlFianza.primas.derechos !== undefined && (
                          <div>
                            <div className="text-gray-500">Derechos</div>
                            <div className="text-gray-800">
                              {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(liveFianzaData.xmlFianza.primas.derechos)}
                            </div>
                          </div>
                        )}
                        {desgloseValido && liveFianzaData.xmlFianza.primas.gastosExpedicion !== undefined && (
                          <div>
                            <div className="text-gray-500">Gastos Exp.</div>
                            <div className="text-gray-800">
                              {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(liveFianzaData.xmlFianza.primas.gastosExpedicion)}
                            </div>
                          </div>
                        )}
                        {desgloseValido && liveFianzaData.xmlFianza.primas.gastosInvestigacion !== undefined && (
                          <div>
                            <div className="text-gray-500">Gastos Inv.</div>
                            <div className="text-gray-800">
                              {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(liveFianzaData.xmlFianza.primas.gastosInvestigacion)}
                            </div>
                          </div>
                        )}
                        {desgloseValido && liveFianzaData.xmlFianza.primas.bonificacion !== undefined && (
                          <div>
                            <div className="text-gray-500">Bonificación</div>
                            <div className="text-gray-800">
                              {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(liveFianzaData.xmlFianza.primas.bonificacion)}
                            </div>
                          </div>
                        )}
                        {desgloseValido && liveFianzaData.xmlFianza.primas.subTotal !== undefined && (
                          <div>
                            <div className="text-gray-500">Subtotal</div>
                            <div className="text-gray-800 font-medium">
                              {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(liveFianzaData.xmlFianza.primas.subTotal)}
                            </div>
                          </div>
                        )}
                        {desgloseValido && liveFianzaData.xmlFianza.primas.iva !== undefined && (
                          <div>
                            <div className="text-gray-500">IVA</div>
                            <div className="text-gray-800">
                              {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(liveFianzaData.xmlFianza.primas.iva)}
                            </div>
                          </div>
                        )}
                        {desgloseValido && liveFianzaData.xmlFianza.primas.primaTotal !== undefined && (
                          <div className="col-span-2 bg-emerald-50 rounded p-1.5 -mx-1.5">
                            <div className="text-emerald-700 font-medium">Prima Total</div>
                            <div className="text-emerald-800 font-bold text-sm">
                              {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(liveFianzaData.xmlFianza.primas.primaTotal)}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <div className="text-orange-400 mb-1">
                      <svg className="w-8 h-8 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                      </svg>
                    </div>
                    <p className="text-gray-500 text-xs">
                      XML de fianza no cargado
                    </p>
                  </div>
                )}
              </div>

            </div>
          </div>

          {/* Comisión y liquidación (owner-only) */}
          <ComisionLedgerPanel
            fianza={liveFianzaData}
            contratoData={contratoData}
            userData={userData}
            onUpdate={onUpdate}
          />
        </div>
      </div>

      {/* Solicitar endoso (forward-looking request) */}
      {!isClient && (
        <SolicitarEndosoDialog
          isOpen={showSolicitar}
          onClose={() => setShowSolicitar(false)}
          fianza={liveFianzaData}
          user={user}
          userData={userData}
          buildBitacoraMeta={buildBitacoraMeta}
          onApplied={onUpdate}
        />
      )}

      {/* Endoso Dialog */}
      {!isClient && (
        <EndosoDialog
          isOpen={showEndoso}
          onClose={() => { setShowEndoso(false); setEndosoRequeridoId(null); }}
          fianza={liveFianzaData}
          contratoData={contratoData}
          clientData={clientData}
          user={user}
          userData={userData}
          buildBitacoraMeta={buildBitacoraMeta}
          endosoRequeridoId={endosoRequeridoId}
          onApplied={() => { if (onUpdate) onUpdate(); }}
        />
      )}

      {/* Renovar Dialog */}
      {!isClient && (
        <RenovarFianzaDialog
          isOpen={showRenovar}
          onClose={() => setShowRenovar(false)}
          fianza={liveFianzaData}
          contratoData={contratoData}
          clientData={clientData}
          user={user}
          userData={userData}
          buildBitacoraMeta={buildBitacoraMeta}
          onApplied={() => { if (onUpdate) onUpdate(); }}
        />
      )}

      {/* Sustituir Dialog */}
      {!isClient && (
        <SustituirFianzaDialog
          isOpen={showSustituir}
          onClose={() => setShowSustituir(false)}
          fianza={liveFianzaData}
          contratoData={contratoData}
          clientData={clientData}
          convenio={convenio}
          user={user}
          userData={userData}
          buildBitacoraMeta={buildBitacoraMeta}
          onApplied={() => { if (onUpdate) onUpdate(); }}
        />
      )}

      {/* XML Confirmation Dialog */}
      <XmlConfirmationDialog
        isOpen={showConfirmDialog}
        onConfirm={confirmPendingUpload}
        onCancel={cancelPendingUpload}
        parsedData={pendingUpload?.parsedData}
        warnings={pendingUpload?.warnings || []}
        fileType={pendingUpload?.fileKey}
        isLoading={isUpdating}
      />

      {/* Associated contract, stacked above this view (later in DOM = on top) */}
      {showContrato && contratoData?.id && (
        <ContratoView
          isOpen={showContrato}
          onClose={() => setShowContrato(false)}
          contrato={contratoData}
          clientData={clientData}
          convenio={convenio}
          beneficiariosById={beneficiariosById}
          onUpdate={onUpdate}
        />
      )}
    </div>
  );
}

export default FianzaView;
